// Scheduler.js
import i18n from "../i18n";
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import config from '../config';
import Schedules from './Schedules';
import ScheduleEmailPanel from './ScheduleEmailPanel';
import './schedule-ui.css';
import { useBpFlags } from '../Responsive';

const tr = (...args) => i18n.t(...args);

const Scheduler = () => {

    const { isTablet } = useBpFlags();
    const viewportRef = useRef(null);
    const contentRef  = useRef(null);
    const [scale, setScale] = useState(1);
    const [selectedWeek, setSelectedWeek] = useState(moment().startOf('isoWeek'));
    const [areas, setAreas] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState({});

    useEffect(() => {
        fetchUsers();
        fetchAreas();
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [areas]);

    useEffect(() => {
        fetchSchedules();
    }, [users, categories, selectedWeek, showModal]);

    // Auto-fit scale on tablet
    useEffect(() => {
      if (!isTablet) { setScale(1); return; }
      const compute = () => {
        const vp = viewportRef.current;
        const el = contentRef.current;
        if (!vp || !el) return;
        const vw = vp.clientWidth - 16;
        const cw = el.scrollWidth;
        const s = Math.max(0.75, Math.min(1, vw / Math.max(1, cw)));
        setScale(s);
      };
      compute();
      window.addEventListener('resize', compute);
      return () => window.removeEventListener('resize', compute);
    }, [isTablet, schedules, categories, users, selectedWeek, showModal]);

    useEffect(() => {
        fetchUsersSchedules();
    }, [schedules]);

    const fetchAreas = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/areas', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setAreas(response.data);
        } catch (error) {
            console.error('Error fetching areas:', error);
        }
    };

    const fetchSchedules = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/schedules', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setSchedules(response.data);
        } catch (error) {
            console.error('Error fetching schedules:', error);
        }
    };

    const fetchUsersSchedules = async () => {
        const weekNumber = selectedWeek.isoWeek();
        const year = selectedWeek.year();

        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users-schedules?week={{weekNumber}}&year={{year}}', {
                API_BASE_URL: config.API_BASE_URL,
                weekNumber,
                year
            }));
            const userSelections = {};
    
            response.data.forEach(({ schedule, user }) => {
                if (!userSelections[schedule]) {
                    userSelections[schedule] = {};
                }
                userSelections[schedule][user] = true; // Mark user as selected
            });
    
            setSelectedUsers(userSelections);
        } catch (error) {
            console.error("Error fetching users' schedules:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users/roles', {
                API_BASE_URL: config.API_BASE_URL
            }));
            const roles = response.data.map(role => ({
                id: role.id,
                name: role.name,
                role: role.role,
                area: null                         
            }));

            setCategories([...roles, ...areas.map(area => ({
                id: area.id,
                name: area.name,
                role: 'at_service',
                area: area.id
            }))]);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleCheckboxChange = (userId, slotId) => {
        setSelectedUsers(prev => ({
            ...prev,
            [slotId]: {
                ...prev[slotId],
                [userId]: !prev[slotId]?.[userId] // Toggle selection
            }
        }));
    };

    const handleWeekChange = (increment) => {
        const newWeek = selectedWeek.clone().add(increment, 'weeks');
        //if (newWeek.isBefore(moment().startOf('isoWeek'))) return;   
        setSelectedWeek(newWeek);
    };

    const handleSaveSchedules = async () => {
        const weekNumber = selectedWeek.isoWeek();
        const year = selectedWeek.year();

        const formattedSchedules = Object.entries(selectedUsers).map(([scheduleId, users]) => ({
            scheduleId,
            userIds: Object.keys(users).filter(userId => users[userId]) // Get only selected users
        }));

        try {
            await axios.post(tr('{{API_BASE_URL}}/users-schedules/bulk', {
                API_BASE_URL: config.API_BASE_URL
            }), {
                weekStart: selectedWeek.format("YYYY-MM-DD"),
                weekEnd: selectedWeek.clone().add(6, "days").format("YYYY-MM-DD"),
                weekNumber,
                year,
                schedules: formattedSchedules
            });
    
            alert("Schedules saved successfully!");
            fetchUsersSchedules(); // Refresh data
        } catch (error) {
            console.error("Error saving users' schedules:", error);
        }
    };

    const handleReplicateSchedules = async () => {
        const direction = selectedWeek.isAfter(moment().startOf("isoWeek")) ? "future" : "past";
        const weekNumber = selectedWeek.isoWeek();
        const year = selectedWeek.year();

        try {
            const res = await axios.post(tr('{{API_BASE_URL}}/schedules/replicate', {
                API_BASE_URL: config.API_BASE_URL
            }), {
                selectedWeek: selectedWeek.format("YYYY-MM-DD"),
                direction,
                week: weekNumber,  
                year: year        
            });
    
            alert(res.data.message);
            fetchUsersSchedules(); // Refresh the data
        } catch (error) {
            console.error("Error replicating user schedules:", error);
            alert("Failed to replicate schedules.");
        }
    };

    const getUsersByRole = (role) => {
        return users.filter(user => user[role]?.data?.[0]);
    };

    const isToday = (day) =>
        selectedWeek.clone().add(day, 'days').format('D/M/Y') === moment().format('D/M/Y');

    const renderTableHeaders = () => {
        return (
            <tr>
                <th className="sch-rowhead">{tr('Role / Area')}</th>
                {[...Array(7)].map((_, i) => {
                    const d = selectedWeek.clone().add(i, 'days');
                    return (
                        <th key={i} className={isToday(i) ? 'today' : ''}>
                            <span className="dow">{d.format('ddd')}</span>
                            {d.format('D MMM')}
                        </th>
                    );
                })}
            </tr>
        );
    };

    const renderTableRows = () => {
        return categories.map((category, rowIndex) => (
            <tr key={rowIndex}>
                <td className="sch-rowhead">{category.name}
                    {category.area ? <span className="sub">{tr('Area')}</span> : null}</td>
                {[...Array(7)].map((_, day) => {
                    //const cellSlots = schedules.filter(slot => slot.area === category.area && slot.role === category.id && slot.day === day);
                    
                   const cellSlots = schedules.filter(slot =>
                     slot.day === day &&
                     (
                       (category.area ? // area row: match by area id only
                       slot.area === category.area : slot.area == null && slot.role === category.id) // role row: no area, match role id
                     )
                   );
                    
                    
                    
                    return (
                        <td key={day} className={isToday(day) ? 'today-col' : ''}>
                            {cellSlots.length === 0 && <div className="sch-empty-cell">–</div>}
                            {cellSlots.map((slot, index) => {
                                const picked = Object.values(selectedUsers[slot.id] || {}).filter(Boolean).length;
                                return (
                                    <div key={index} className={`sch-slot${picked ? ' mine' : ''}`}>
                                        <div className="sch-slot-time">
                                            {slot.starts.slice(0, -3)} – {slot.ends.slice(0, -3)}
                                            {picked > 0 && <span style={{ float: 'right', fontWeight: 400 }}>{picked}</span>}
                                        </div>
                                        <div className="sch-pick">
                                            {getUsersByRole(category.role).map(u => {
                                                const on = !!selectedUsers[slot.id]?.[u.id];
                                                return (
                                                    <label key={u.id} className={on ? 'on' : ''}>
                                                        <input type="checkbox" value={u.id} checked={on}
                                                            onChange={() => handleCheckboxChange(u.id, slot.id)} />
                                                        {u.name || '(' + u.username + ')'}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </td>
                    );
                })}
            </tr>
        ));
    };

    const isAdmin = (() => {
        try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            const v = u.view_admin;
            return v === 1 || v === true || (v && v.data && v.data[0] === 1);
        } catch { return false; }
    })();

    return (
        <div className={isTablet ? 'tablet-viewport' : ''} ref={viewportRef}>
          <div className="sch-page">
            <div className="sch-head">
                <h2>{tr('Weekly Scheduler')}</h2>
                <div className="sch-weeknav">
                    <button onClick={() => handleWeekChange(-1)} title={tr('Previous Week')}>⏴</button>
                    <span className={`sch-weeklabel${selectedWeek.isSame(moment().startOf('isoWeek'), 'day') ? ' is-current' : ''}`}
                          title={tr('Week') + ': ' + selectedWeek.isoWeek()}>
                        {selectedWeek.format('D MMM')} – {selectedWeek.clone().add(6, 'days').format('D MMM YYYY')}
                    </span>
                    <button onClick={() => handleWeekChange(1)} title={tr('Next Week')}>⏵</button>
                </div>
                {!selectedWeek.isSame(moment().startOf('isoWeek'), 'day') && (
                    <button className="sch-btn ghost" onClick={() => setSelectedWeek(moment().startOf('isoWeek'))}>
                        {tr('Current Week')}
                    </button>
                )}
                <span className="sch-spacer" />
                <button className="sch-btn" onClick={() => setShowModal(true)}>⚙ {tr('Slots Settings')}</button>
                <button className="sch-btn" onClick={handleReplicateSchedules}>⎘ {tr('Replicate')}</button>
                <button className="sch-btn primary" onClick={handleSaveSchedules}>💾 {tr('Save')}</button>
            </div>

            <div className="sch-table-wrap">
                <table className="sch-table">
                    <thead>{renderTableHeaders()}</thead>
                    <tbody>{renderTableRows()}</tbody>
                </table>
            </div>

            <ScheduleEmailPanel isAdmin={isAdmin} />
          </div>

          {showModal && (
              <div className="modal-overlay">
                  <div className="modal-content">
                      <Schedules closeModal={() => setShowModal(false)} />
                  </div>
              </div>
          )}
        </div>
    );
};

export default Scheduler;
